/**
 * pz-pdp-country-check.js — выбор страны и вердикт активации ключа в
 * раскрывашке «Check country restrictions» мобильной карточки товара
 * (snippets/pz-pdp-country-check.liquid, PZ-777).
 *
 * Логика ОБЩАЯ с десктопным окном «Activation check» (prod-extras в
 * templates/product.json, PZ-783): правило страны, список, поиск, лучшее
 * совпадение и гео ПК-окно берёт отсюда динамическим import() по
 * asset_url. Копий в шаблоне не заводить — копия держала allow-first,
 * и на странах из обоих списков ПК и телефон расходились (сторожит
 * тест «нет копии в шаблоне»). Интерфейсы у поверхностей свои:
 *  - телефон — свой список (listbox) вместо native <select>: на iOS select
 *    открывает колесо внизу экрана, а не прокручиваемый список;
 *  - правило страны — block-first, как snippets/pz-can-activate.liquid
 *    (строка «Activates in…» над раскрывашкой); одно на карточку;
 *  - телефон: строки переведены (data-атрибуты из Liquid через | t), имена
 *    стран — на языке страницы; ПК пока английский.
 *
 * Модуль: чистые функции экспортируются для node --test
 * (.github/tools/test_pz_pdp_country_check.mjs) и для ПК-окна, DOM трогает
 * только setupCountryCheck. Импорт без побочных эффектов там, где нет
 * [data-pz-country-check]: автозапуск ниже ищет только свои корни.
 * Один файл, а не «логика + интерфейс» двумя модулями: относительный
 * import теряет ?v= адреса ассета, и Shopify отдал бы закэшированную на
 * год старую логику (гоча PZ-556, docs/maps/pdp.md).
 */

// Список стран обеих поверхностей (телефон и ПК-окно prod-extras).
// Страну посетителя, если её здесь нет, добавляют ensureCountry / ensureCode.
export const COUNTRY_CODES = 'AD AE AF AG AI AL AM AO AR AT AU AW AZ BA BB BD BE BF BG BH BI BJ BN BO BR BS BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR CU CV CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM FR GA GB GD GE GH GM GN GQ GR GT GW GY HK HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MH MK ML MM MN MO MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NR NZ OM PA PE PG PH PK PL PT PW PY QA RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SY SZ TD TG TH TJ TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VC VE VN VU WS YE ZA ZM ZW'.split(' ');

// Ключ и формат кэша ({c: ISO, t: мс}) — ОБЩИЕ с десктопным окном:
// страна, найденная на одной поверхности, не запрашивается на другой.
export const GEO_CACHE_KEY = 'pz_geo_v2';
export const GEO_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const GEO_TIMEOUT_MS = 4000;
const REGIONAL_INDICATOR_OFFSET = 127397;
// Скринридер слышит вердикт после паузы в наборе, а не на каждую букву.
const ANNOUNCE_DELAY_MS = 400;
// Плейсхолдеры, которые Liquid передал в | t (snippets/pz-pdp-country-check.liquid).
const COUNTRY_TOKEN = '%%country%%';
const QUERY_TOKEN = '%%query%%';

export const isCountryCode = (code) => typeof code === 'string' && /^[A-Z]{2}$/.test(code);

/** JSON-массив кодов из атрибута → коды в верхнем регистре; мусор → []. */
export function parseCountryList(json) {
  let list;
  try {
    list = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(list)) return [];
  const codes = list.map((code) => String(code).trim().toUpperCase());
  // Сентинел "ALL" = «без ограничений по allow». Сниппет его уже снимает,
  // здесь — страховка: иначе allow из одного "ALL" не пустил бы никого.
  return codes.includes('ALL') ? [] : codes;
}

/**
 * Правило snippets/pz-can-activate.liquid, один в один (block-first):
 * страна в block — нет; непустой allow без страны — нет; иначе да.
 * Меняешь здесь — меняй там (и наоборот): строка «Activates in…» и
 * вердикт выбора стоят на одном экране.
 */
export function canActivateIn(code, allow, block) {
  const country = String(code || '').toUpperCase();
  if (block.includes(country)) return false;
  if (allow.length && !allow.includes(country)) return false;
  return true;
}

export function getFlagEmoji(code) {
  const country = String(code || '').toUpperCase();
  if (country.length !== 2) return '';
  return country.replace(/./g, (letter) => String.fromCodePoint(REGIONAL_INDICATOR_OFFSET + letter.charCodeAt(0)));
}

// Без регистра, латинской диакритики и пунктуации: «cote d ivoire» находит
// «Côte d’Ivoire», «bosnia herzegovina» — «Bosnia & Herzegovina».
// Буквы и знаки (\p{M}) любых письменностей остаются: имена стран идут на
// языке страницы, а тайские гласные и арабские огласовки — это \p{M}.
export function normalizeCountryText(text) {
  let normalized = String(text || '').toLowerCase();
  try {
    normalized = normalized.normalize('NFD').replace(/[̀-ͯ]/g, '');
  } catch {
    /* старый движок — сравниваем без снятия диакритики */
  }
  return normalized.replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ').trim();
}

/** (код) → имя страны на языке locale; нет Intl.DisplayNames — сам код. */
export function createCountryNameLookup(locale) {
  let displayNames = null;
  try {
    displayNames = new Intl.DisplayNames([locale, 'en'], { type: 'region' });
  } catch {
    /* нет Intl.DisplayNames или битый lang — показываем коды */
  }
  return (code) => {
    try {
      return (displayNames && displayNames.of(code)) || code;
    } catch {
      return code;
    }
  };
}

const getEnglishCountryName = createCountryNameLookup('en');

/**
 * Страна для списка: имя на языке страницы и ключи поиска. Английское
 * имя тоже ищется — латиницей набирают и на русской, и на тайской витрине.
 */
export function createCountry(code, getCountryName) {
  const name = getCountryName(code);
  const keys = [...new Set([normalizeCountryText(name), normalizeCountryText(getEnglishCountryName(code))])];
  return { code, name, keys };
}

/** Страны, подошедшие под запрос; пустой запрос — все. */
export function findCountryMatches(countries, query) {
  const needle = normalizeCountryText(query);
  if (!needle) return countries.slice();
  // ISO-код наравне с именем: «ua», «PL» набрать быстрее.
  return countries.filter((country) => country.code.toLowerCase() === needle || country.keys.some((key) => key.includes(needle)));
}

const rankKey = (key, needle) => {
  if (key === needle) return 0;
  if (key.startsWith(needle)) return 1;
  if (key.includes(` ${needle}`)) return 2;
  return 3;
};

/** Лучшее совпадение: точное имя/код → начало имени → начало слова → вхождение. */
export function pickBestMatch(matches, query) {
  const needle = normalizeCountryText(query);
  const rankOf = (country) =>
    country.code.toLowerCase() === needle ? 0 : Math.min(...country.keys.map((key) => rankKey(key, needle)));
  const ranked = matches
    .map((country) => ({ country, rank: rankOf(country) }))
    .sort((a, b) => a.rank - b.rank || a.country.keys[0].localeCompare(b.country.keys[0]));
  return ranked.length ? ranked[0].country.code : '';
}

export const fillTemplate = (template, token, value) => String(template || '').split(token).join(value);

// ——— Страна посетителя ———
// Штатный same-origin эндпоинт Shopify /browsing_context_suggestions.json
// (detected_values.country по гео-IP; свой гео-сервис не нужен — решение
// Евгения 2026-07-16). Им же пользуется ПК-окно.

/** Код из кэша pz_geo_v2 или null (нет, протух, битый, приватный режим). */
export function readCachedGeo(storage = globalThis.sessionStorage, now = Date.now()) {
  try {
    const cached = JSON.parse(storage.getItem(GEO_CACHE_KEY));
    if (cached && isCountryCode(cached.c) && now - cached.t < GEO_CACHE_TTL_MS) return cached.c;
  } catch {
    /* нет хранилища или битое значение */
  }
  return null;
}

export function writeCachedGeo(code, storage = globalThis.sessionStorage, now = Date.now()) {
  try {
    storage.setItem(GEO_CACHE_KEY, JSON.stringify({ c: code, t: now }));
  } catch {
    /* кэш не обязателен */
  }
}

/**
 * ISO-код страны посетителя или null (эндпоинт не дал код). Сеть упала
 * или 4 с без ответа — promise отклоняется: вызывающий решает, что
 * показать. fetchResource и storage подменяет только тест.
 */
export async function detectVisitorCountry(fetchResource = globalThis.fetch, storage = globalThis.sessionStorage) {
  const cached = readCachedGeo(storage);
  if (cached) return cached;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GEO_TIMEOUT_MS);
  try {
    const response = await fetchResource('/browsing_context_suggestions.json', {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    const body = response.ok ? await response.json() : null;
    const detected = body && body.detected_values && body.detected_values.country;
    const code = detected && detected.handle ? String(detected.handle).toUpperCase() : '';
    if (!isCountryCode(code)) return null;
    writeCachedGeo(code, storage);
    return code;
  } finally {
    clearTimeout(timer);
  }
}

// ——— Интерфейс ———

const VERDICT_KIND_CLASS = {
  yes: 'pz-pdp__check-verdict--yes',
  no: 'pz-pdp__check-verdict--no',
  none: 'pz-pdp__check-verdict--none',
};
const VERDICT_MARK = { yes: '✓  ', no: '✕  ', none: '' };

function createState(root, detectCountry) {
  const locale = document.documentElement.lang || 'en';
  return {
    root,
    locale,
    detectCountry,
    getCountryName: createCountryNameLookup(locale),
    input: root.querySelector('[data-pz-check-input]'),
    list: root.querySelector('[data-pz-check-list]'),
    verdict: root.querySelector('[data-pz-check-verdict]'),
    status: root.querySelector('[data-pz-check-status]'),
    detectedLine: root.querySelector('[data-pz-check-detected]'),
    details: root.closest('details'),
    allow: parseCountryList(root.dataset.pzAllow),
    block: parseCountryList(root.dataset.pzBlock),
    countries: [],
    optionByCode: new Map(),
    selectedCode: '',
    isCountryPicked: false,
    isBuilt: false,
    announceTimer: 0,
  };
}

const formatCountryLabel = (state, code) => `${state.getCountryName(code)} ${getFlagEmoji(code)}`;

function announce(state, text) {
  clearTimeout(state.announceTimer);
  state.announceTimer = setTimeout(() => {
    state.status.textContent = text;
  }, ANNOUNCE_DELAY_MS);
}

function renderVerdict(state, kind, text) {
  const { verdict } = state;
  verdict.className = `pz-pdp__check-verdict ${VERDICT_KIND_CLASS[kind]}`;
  verdict.textContent = VERDICT_MARK[kind] + text;
  verdict.hidden = false;
  announce(state, text);
}

function renderDetected(state, code) {
  const [before, after = ''] = String(state.root.dataset.pzTextDetected || '').split(COUNTRY_TOKEN);
  const name = document.createElement('b');
  name.textContent = formatCountryLabel(state, code);
  state.detectedLine.replaceChildren(before, name, after);
  state.detectedLine.hidden = false;
}

function createOption(country, listId) {
  // <button>, а не <li>: тап и фокус работают без своих обработчиков,
  // role="option" держит семантику listbox для скринридера.
  // tabindex=-1 — иначе ~200 остановок Tab; по списку ходят стрелками из поля.
  const option = document.createElement('button');
  option.type = 'button';
  option.className = 'pz-pdp__check-option';
  option.id = `${listId}-${country.code}`;
  option.tabIndex = -1;
  option.setAttribute('role', 'option');
  option.setAttribute('aria-selected', 'false');
  option.dataset.code = country.code;
  option.textContent = `${getFlagEmoji(country.code)}  ${country.name}`;
  return option;
}

function insertCountry(state, country, index) {
  const next = state.countries[index];
  state.countries.splice(index, 0, country);
  const option = createOption(country, state.list.id);
  state.optionByCode.set(country.code, option);
  state.list.insertBefore(option, next ? state.optionByCode.get(next.code) : null);
}

// Страна посетителя вне списка CODES (Косово, Гибралтар…) — вставляем по
// алфавиту, как ensureCode у ПК, иначе её нельзя выбрать по умолчанию.
function ensureCountry(state, code) {
  if (!isCountryCode(code) || state.optionByCode.has(code)) return;
  const country = createCountry(code, state.getCountryName);
  const index = state.countries.findIndex((other) => other.name.localeCompare(country.name, state.locale) > 0);
  insertCountry(state, country, index < 0 ? state.countries.length : index);
}

// Список строится при первом раскрытии: ~200 кнопок в закрытой
// раскрывашке на каждом просмотре карточки не нужны.
function buildCountryOptions(state) {
  const fragment = document.createDocumentFragment();
  state.countries = COUNTRY_CODES.map((code) => createCountry(code, state.getCountryName));
  state.countries.sort((a, b) => a.name.localeCompare(b.name, state.locale));
  state.countries.forEach((country) => {
    const option = createOption(country, state.list.id);
    state.optionByCode.set(country.code, option);
    fragment.append(option);
  });
  state.list.append(fragment);
  state.input.setAttribute('aria-expanded', 'true');
  state.isBuilt = true;
}

// Прокрутка ВНУТРИ списка, а не scrollIntoView: тот двигал бы и страницу,
// уводя поле поиска из-под пальца. offsetTop считается от списка
// (position: relative в стилях сниппета).
function revealOption(list, option, isCentered) {
  const top = option.offsetTop;
  const bottom = top + option.offsetHeight;
  if (isCentered) {
    list.scrollTop = top - (list.clientHeight - option.offsetHeight) / 2;
  } else if (top < list.scrollTop) {
    list.scrollTop = top;
  } else if (bottom > list.scrollTop + list.clientHeight) {
    list.scrollTop = bottom - list.clientHeight;
  }
}

function markSelected(state, code) {
  state.optionByCode.forEach((option, optionCode) => {
    option.setAttribute('aria-selected', String(optionCode === code));
  });
  const option = state.optionByCode.get(code);
  if (option) state.input.setAttribute('aria-activedescendant', option.id);
  else state.input.removeAttribute('aria-activedescendant');
  return option;
}

function selectCountry(state, code, isCentered) {
  const option = markSelected(state, code);
  if (!option) return;
  state.selectedCode = code;
  const isAllowed = canActivateIn(code, state.allow, state.block);
  const template = isAllowed ? state.root.dataset.pzTextYes : state.root.dataset.pzTextNo;
  renderVerdict(state, isAllowed ? 'yes' : 'no', fillTemplate(template, COUNTRY_TOKEN, formatCountryLabel(state, code)));
  revealOption(state.list, option, isCentered);
}

/** Прячет страны вне выдачи; aria-expanded — по факту видимости списка. */
function applyFilter(state, query) {
  const matches = findCountryMatches(state.countries, query);
  const visible = new Set(matches.map((country) => country.code));
  state.optionByCode.forEach((option, code) => {
    option.hidden = !visible.has(code);
  });
  state.list.hidden = matches.length === 0;
  state.input.setAttribute('aria-expanded', String(matches.length > 0));
  return matches;
}

function renderNotFound(state, query) {
  markSelected(state, '');
  renderVerdict(state, 'none', fillTemplate(state.root.dataset.pzTextNotFound, QUERY_TOKEN, query.trim()));
}

// Поле поиска — сам выбор страны (как у ПК): лучшее совпадение
// выбирается сразу, без второго тапа по списку.
function handleSearchInput(state) {
  const query = state.input.value;
  const matches = applyFilter(state, query);
  if (!query.trim()) {
    if (state.selectedCode) selectCountry(state, state.selectedCode, true);
    return;
  }
  state.isCountryPicked = true;
  if (!matches.length) {
    renderNotFound(state, query);
    return;
  }
  state.list.scrollTop = 0;
  selectCountry(state, pickBestMatch(matches, query), false);
}

function bindSearch(state) {
  state.input.addEventListener('input', () => handleSearchInput(state));
  state.list.addEventListener('click', (event) => {
    const option = event.target.closest('[data-code]');
    if (!option) return;
    state.isCountryPicked = true;
    selectCountry(state, option.dataset.code, false);
  });
}

function moveSelection(state, step) {
  const visible = [...state.optionByCode.values()].filter((option) => !option.hidden);
  if (!visible.length) return;
  const index = visible.findIndex((option) => option.dataset.code === state.selectedCode);
  const next = visible[Math.min(Math.max(index + step, 0), visible.length - 1)];
  state.isCountryPicked = true;
  selectCountry(state, next.dataset.code, false);
}

// Стрелки ходят по видимым странам, Enter прячет клавиатуру телефона.
function bindArrowKeys(state) {
  state.input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      state.input.blur();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveSelection(state, event.key === 'ArrowDown' ? 1 : -1);
    }
  });
}

function applyDetectedCountry(state, code) {
  if (!code) return;
  renderDetected(state, code);
  ensureCountry(state, code);
  if (!state.isCountryPicked) selectCountry(state, code, true);
}

// Первое раскрытие: список, страна рынка из Liquid, затем запрос гео
// (не на загрузке — раскрывашку открывает малая доля покупателей).
function openForFirstTime(state) {
  buildCountryOptions(state);
  const fallbackCode = String(state.root.dataset.pzFallbackCountry || '').toUpperCase();
  ensureCountry(state, fallbackCode);
  selectCountry(state, fallbackCode, true);
  state.detectCountry()
    .then((code) => applyDetectedCountry(state, code))
    .catch(() => {
      /* гео не ответил — остаётся страна рынка */
    });
}

function bindGeoOnFirstOpen(state) {
  const onOpen = () => {
    if (state.details && !state.details.open) return;
    if (!state.isBuilt) openForFirstTime(state);
    else if (state.selectedCode) revealOption(state.list, state.optionByCode.get(state.selectedCode), true);
  };
  if (state.details) state.details.addEventListener('toggle', onOpen);
  if (!state.details || state.details.open) onOpen();
}

export function setupCountryCheck(root, detectCountry) {
  if (root.dataset.pzCountryCheckReady) return;
  root.dataset.pzCountryCheckReady = '1';
  const state = createState(root, detectCountry);
  if (!state.input || !state.list || !state.verdict || !state.status || !state.detectedLine) return;
  bindSearch(state);
  bindArrowKeys(state);
  bindGeoOnFirstOpen(state);
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-pz-country-check]').forEach((root) => setupCountryCheck(root, detectVisitorCountry));
}
